import React, { useState, useEffect, useContext, useRef } from 'react'
import { useParams } from "react-router-dom";
import { Box, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CurrentActivity from '../components/currentActivity';
import Recall from '../components/recall';
import BottomSheet from '../components/bottomSheet';
import BookingDetails from '../components/bookingDetails';
import { StaticValetActivities, getActivityConfig } from '../utils/shareData';
import config from "../config.json";
import VisibleActivities from '../components/visibleActivities';
import Activities from '../components/activities';
import AdditionalInfo from '../components/additionalInfo';
import { Feedback, FeedbackHandler } from '../components/feedback';
import Payments from '../components/payment';
import { GlobalStateContext } from '../context/globalStateProvider';
import moment from 'moment';
import Helmet from 'react-helmet';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
// import ImageViewer from '../components/imageViewer';
import ExitInfo from '../components/exitInfo';
import { toast } from 'react-toastify';

import PaymentInfo from '../components/paymentInfo';
import { parseTimestamp } from '../utils/dateTime';
import LiveTrackingMap from '../components/liveTrackingMap';
const androidAppId = 'in.parksmart.user'
const iosAppId = 'id1538546356'
const RETRIEVAL_TRIP = 'RETRIEVAL';
const VALET_TRIP_API_PATH = '/user/valet/retrieval-trip';
const TRACKING_CACHE_TTL_MS = 15 * 60 * 1000;
const ENABLE_BOOKING_POLLING = true;

const toCoordinate = (latitude, longitude) => {
  if (
    latitude === null ||
    latitude === undefined ||
    latitude === '' ||
    longitude === null ||
    longitude === undefined ||
    longitude === ''
  ) return null;

  const lat = Number(latitude);
  const lng = Number(longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const toOptionalNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const getTrackingCacheKey = (bookingId, valetServiceOrderId) => (
  `valet-tracking:${bookingId}:${valetServiceOrderId}`
);

const readTrackingCache = (bookingId, valetServiceOrderId) => {
  try {
    const cachedValue = JSON.parse(
      window.sessionStorage.getItem(getTrackingCacheKey(bookingId, valetServiceOrderId))
    );
    const cachedAt = toOptionalNumber(cachedValue?.cachedAt);

    if (!cachedValue || !cachedAt || Date.now() - cachedAt > TRACKING_CACHE_TTL_MS) {
      window.sessionStorage.removeItem(getTrackingCacheKey(bookingId, valetServiceOrderId));
      return null;
    }

    return {
      currentLocation: toCoordinate(cachedValue.currentLocation?.lat, cachedValue.currentLocation?.lng),
      etaSeconds: toOptionalNumber(cachedValue.etaSeconds),
      locationCachedAt: cachedAt,
    };
  } catch {
    return null;
  }
};

const clearTrackingCache = (bookingId, valetServiceOrderId) => {
  if (!bookingId || !valetServiceOrderId) return;
  try {
    window.sessionStorage.removeItem(getTrackingCacheKey(bookingId, valetServiceOrderId));
  } catch {
    // Tracking remains functional when browser storage is unavailable.
  }
};

const getDestinationLocation = (...sources) => {
  for (const source of sources) {
    const location = toCoordinate(
      source?.endLatitude ?? source?.destinationLatitude,
      source?.endLongitude ?? source?.destinationLongitude
    );
    if (location) return location;
  }
  return null;
};

const createTrackingState = (valetTrip, fallbackDestination = null, fallbackTracking = null) => {
  if (valetTrip?.tripType !== RETRIEVAL_TRIP) return null;

  const currentLocation = toCoordinate(
    valetTrip.currentLatitude ?? valetTrip.currentLat,
    valetTrip.currentLongitude ?? valetTrip.currentLng ?? valetTrip.currentLong
  );

  return {
    startLocation: toCoordinate(valetTrip.startLatitude, valetTrip.startLongitude),
    destination: getDestinationLocation(valetTrip) ?? fallbackDestination,
    currentLocation: currentLocation ?? fallbackTracking?.currentLocation ?? null,
    etaSeconds: toOptionalNumber(valetTrip.etaSeconds) ?? fallbackTracking?.etaSeconds ?? null,
    // Preserve the original age when this is a cached fallback, not a fresh GPS coordinate.
    locationCachedAt: currentLocation ? Date.now() : fallbackTracking?.locationCachedAt ?? null,
    status: valetTrip.status,
    tripType: valetTrip.tripType,
  };
};

const Main = () => {
  const theme = useTheme();
  const { id } = useParams();

  const { position, setInstalledApps, setIsAndroidAppInstalled, setIsIosAppInstalled, setIsSupported, setError } = useContext(GlobalStateContext)
  const [loading, setLoading] = useState(false)

  const [currentActivity, setCurrentActivity] = useState(null)
  const [activities, setActivities] = useState(null)

  const [bookingDetails, setBookingDetails] = useState(null)
  console.log("Booking Details", bookingDetails)

  const [activitiesPercentage, setActivitiesPercentage] = useState(null)
  const [visibleActivities, setVisibleActivities] = useState(null)

  const [allowPayment, setAllowPayment] = useState(false)
  const [showUserInfo, setShowUserInfo] = useState(false)
  const [showPaymentInfo, setShowPaymentInfo] = useState(false)
  const [isPaid, setIsPaid] = useState(false)
  const [remainingAmount, setRemainingAmount] = useState(0)
  const [rating, setRating] = useState(0)
  const [recallConfig, setRecallConfig] = useState({})
  const [showMap, setShowMap] = useState(false)
  const [tracking, setTracking] = useState(null)
  const fetchBookingRef = useRef(null)
  const fetchTrackingRef = useRef(null)
  const isBookingRequestInFlightRef = useRef(false)
  const isTrackingRequestInFlightRef = useRef(false)
  console.log("Tracking Info", tracking)

 const fetchData = async ({ silent = false } = {}) => {
  if (isBookingRequestInFlightRef.current) {
    console.info('Skipping booking refresh because the previous request is still in progress.');
    return;
  }

  isBookingRequestInFlightRef.current = true;

  if (!silent) {
    resetStates();
    setLoading(true);
  }

  try {
    const response = await fetch(
      `${config.HOST_NAME}/user/valet/${id}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    let result;

    try {
      result = await response.json();
    } catch {
      throw new Error('Invalid server response');
    }

    if (!response.ok) {
      throw new Error(result?.message || 'Server error occurred');
    }

    if (result?.responseCode !== 'SUCCESS') {
      throw new Error(result?.message || 'Unable to get valet booking details');
    }

    const bookingDetailsData = result?.data;

    if (!bookingDetailsData) {
      throw new Error('Booking data not found');
    }

    const activities = bookingDetailsData?.activities || [];

    if (!activities.length) {
      throw new Error('No valet activity found');
    }

    const normalizedActivities = activities.map((activity, index) => {
      const parsedTimestamp = parseTimestamp(activity.timestamp);
      const normalizedTimestamp = parsedTimestamp ? parsedTimestamp.toISOString() : "";

      return {
        ...activity,
        id: index + 1,
        timestamp: normalizedTimestamp,
        currentlyOngoing: Boolean(activity.currentlyOngoing),
        completed: Boolean(normalizedTimestamp),
      };
    });

    const currentActivity =
      normalizedActivities.find((a) => a.currentlyOngoing) ||
      normalizedActivities.find((a) => !a.completed) ||
      normalizedActivities[normalizedActivities.length - 1];

    const completedActivities = normalizedActivities.filter(
      (a) => a.completed
    ).length;

    const activitiesPercentage =
      (completedActivities / normalizedActivities.length) * 100;

    const lastDoneIndex = normalizedActivities.reduce((acc, status, index) => {
      return status.completed ? index : acc;
    }, -1);

    const startIndex =
      lastDoneIndex !== -1
        ? Math.max(0, lastDoneIndex - 1)
        : 0;

    const endIndex = Math.min(
      startIndex + 3,
      normalizedActivities.length
    );

    const visibleActivities = normalizedActivities.slice(
      startIndex,
      endIndex
    );

    setCurrentActivity(currentActivity);
    setActivitiesPercentage(activitiesPercentage);
    setVisibleActivities(visibleActivities);
    setActivities(normalizedActivities);

    const booking = bookingDetailsData?.booking || null;
    const valetConfig = bookingDetailsData?.config || {};
    const valetTrip = booking?.valetTrip || null;

    setBookingDetails(booking);
    setShowUserInfo(valetConfig?.showUserInfo === true || valetConfig?.showUserInfo === 'true');
    setShowPaymentInfo(valetConfig?.showPaymentInfo === true || valetConfig?.showPaymentInfo === 'true');
    setShowMap(valetConfig?.allowMapTracking === true || valetConfig?.allowMapTracking === 'true');
    const destination = getDestinationLocation(valetTrip, bookingDetailsData, booking);
    const isRetrievalTrip = valetTrip?.tripType === RETRIEVAL_TRIP;
    const isCompletedTrip = valetTrip?.status === 'COMPLETED';
    const cachedTracking = isRetrievalTrip && !isCompletedTrip && booking?.valetServiceOrderId
      ? readTrackingCache(id, booking.valetServiceOrderId)
      : null;

    if (!isRetrievalTrip) clearTrackingCache(id, booking?.valetServiceOrderId);
    if (isCompletedTrip) clearTrackingCache(id, booking?.valetServiceOrderId);
    setTracking(createTrackingState(valetTrip, destination, cachedTracking));
    setRating(booking?.rating || 0);
    setAllowPayment(Boolean(valetConfig?.allowPayment));
    
    const remainingAmount = Math.max(0, Number(booking?.payment?.dueAmount) || 0);
    const isFullyPaid = remainingAmount === 0;

    setIsPaid(isFullyPaid);
    setRemainingAmount(remainingAmount);

    const recallTime = +valetConfig?.recallTimeMinutes || 15;
    const recallConfig = {
      allowRecall: false,
      recallTime,
      recallTimeInSeconds: recallTime * 60,
      remainingSeconds: 0,
    };

    if (valetConfig?.allowRecall) recallConfig.allowRecall = true;
    const recallActivity = normalizedActivities.find((a) => a.event === "Recalled");

    if (recallActivity) {
      const recallMoment = parseTimestamp(recallActivity.timestamp);
      if (recallMoment) {
        const addedTime = recallMoment.add(recallTime, "minutes");
        const remainingSeconds = addedTime.diff(moment(), "seconds");
        recallConfig.remainingSeconds = remainingSeconds > 0 ? remainingSeconds : 0;
      }
    }
    setRecallConfig(recallConfig);

  } catch (error) {
    console.error('Fetch Error:', error);
    if (!silent) toast.error(error.message || 'Something went wrong');
  } finally {
    isBookingRequestInFlightRef.current = false;
    if (!silent) setLoading(false);
  }
};

  const fetchTrackingData = async () => {
    const valetServiceOrderId = bookingDetails?.valetServiceOrderId;
    if (!showMap || !valetServiceOrderId) return;
    if (isTrackingRequestInFlightRef.current) {
      console.info('Skipping tracking poll because the previous request is still in progress.');
      return;
    }

    isTrackingRequestInFlightRef.current = true;

    try {
      console.info('Polling retrieval trip data...', { valetServiceOrderId });
      const response = await fetch(
        `${config.HOST_NAME}${VALET_TRIP_API_PATH}?vehicleServiceOrderId=${encodeURIComponent(valetServiceOrderId)}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) throw new Error('Unable to refresh valet trip');

      const result = await response.json();
      if (result?.responseCode && result.responseCode !== 'SUCCESS') {
        throw new Error(result?.message || 'Unable to refresh valet trip');
      }
      const responseData = result?.data ?? result;
      const valetTrip = responseData?.valetTrip ?? responseData;
      console.info('Retrieved valet trip polling data:', valetTrip);

      if (valetTrip?.tripType !== RETRIEVAL_TRIP || valetTrip?.status === 'COMPLETED') {
        clearTrackingCache(id, valetServiceOrderId);
      }
      setTracking((previous) => createTrackingState(
        valetTrip,
        previous?.destination ?? getDestinationLocation(bookingDetails),
        previous
      ));
    } catch (error) {
      console.warn('Unable to refresh valet tracking:', error.message);
    } finally {
      isTrackingRequestInFlightRef.current = false;
    }
  };


  const resetStates = () => {
    setCurrentActivity(null)
    setActivities(null)
    setBookingDetails(null)
    setActivitiesPercentage(null)
    setVisibleActivities(null)
    setAllowPayment(false)
    setShowUserInfo(false)
    setShowPaymentInfo(false)
    setIsPaid(false)
    setShowMap(false)
    setTracking(null)
    setRemainingAmount(0)
  }

  const isAppInstalled = () => {
    if ('getInstalledRelatedApps' in navigator) {
      setIsSupported(true);
      navigator.getInstalledRelatedApps()
        .then((apps) => {
          setInstalledApps(apps);
          const androidInstalled = apps.some((app) => app.platform === 'play' && app.id === androidAppId);
          setIsAndroidAppInstalled(androidInstalled);
          const iosInstalled = apps.some((app) => app.platform === 'itunes' && app.id === iosAppId);
          setIsIosAppInstalled(iosInstalled);
        })
        .catch((err) => {
          setError(err.message);
          console.error('Error checking installed apps:', err);
        });
    } else {
      setError('getInstalledRelatedApps API not supported');
    }
  }

  useEffect(() => {
    isAppInstalled()
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isTrackingEvent = ['Recalled', 'Arrived'].includes(currentActivity?.event);
  const shouldShowTrackingMap = showMap && isTrackingEvent;
  const shouldPollTracking = showMap && isTrackingEvent && tracking?.status !== 'COMPLETED';

  fetchBookingRef.current = fetchData;
  fetchTrackingRef.current = fetchTrackingData;

  useEffect(() => {
    if (!ENABLE_BOOKING_POLLING) return undefined;

    let interval = null;

    const stopPolling = () => {
      if (!interval) return;
      window.clearInterval(interval);
      interval = null;
    };

    const startPolling = (refreshImmediately = false) => {
      if (document.visibilityState !== 'visible' || !document.hasFocus() || interval) return;

      if (refreshImmediately) fetchBookingRef.current({ silent: true });
      interval = window.setInterval(() => {
        fetchBookingRef.current({ silent: true });
      }, 30000);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && document.hasFocus()) startPolling(true);
      else stopPolling();
    };
    const handleFocus = () => startPolling(true);

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', stopPolling);
    startPolling();

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', stopPolling);
    };
  }, []);

  useEffect(() => {
    const valetServiceOrderId = bookingDetails?.valetServiceOrderId;
    if (
      tracking?.tripType !== RETRIEVAL_TRIP ||
      tracking?.status === 'COMPLETED' ||
      !tracking?.currentLocation ||
      !tracking?.locationCachedAt ||
      !valetServiceOrderId
    ) return;

    try {
      window.sessionStorage.setItem(
        getTrackingCacheKey(id, valetServiceOrderId),
        JSON.stringify({
          currentLocation: tracking.currentLocation,
          etaSeconds: tracking.etaSeconds,
          cachedAt: tracking.locationCachedAt,
        })
      );
    } catch {
      // Tracking remains functional when browser storage is unavailable.
    }
  }, [bookingDetails?.valetServiceOrderId, id, tracking]);

  useEffect(() => {
    if (!shouldPollTracking) return undefined;

    let interval = null;

    const stopPolling = () => {
      if (!interval) return;
      window.clearInterval(interval);
      interval = null;
    };

    const startPolling = () => {
      if (document.visibilityState !== 'visible' || !document.hasFocus() || interval) return;

      fetchTrackingRef.current();
      interval = window.setInterval(() => {
        fetchTrackingRef.current();
      }, 10000);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && document.hasFocus()) startPolling();
      else stopPolling();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', startPolling);
    window.addEventListener('blur', stopPolling);
    startPolling();

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', startPolling);
      window.removeEventListener('blur', stopPolling);
    };
  }, [shouldPollTracking]);

  const hasPendingPayment = !isPaid || remainingAmount > 0
  const shouldShowRecall = recallConfig?.allowRecall && (!hasPendingPayment || !allowPayment)
  const shouldShowPayment = hasPendingPayment && allowPayment
  const shouldShowPaymentAtExitInfo = hasPendingPayment && !allowPayment

  return (
    <Box sx={theme.customStyles.mainContainer}>
      <Helmet>
        <script src="https://checkout.razorpay.com/v1/checkout.js"></script>
      </Helmet>
      {!loading && currentActivity && bookingDetails ?
        <>
          {shouldShowTrackingMap
            ?  <LiveTrackingMap
            tracking={tracking}
            parkingName={bookingDetails?.site.name || '-'}
            activityLabel={getActivityConfig(currentActivity.event).label}
            currentEvent={currentActivity.event}
            recallConfig={recallConfig}
            etaSeconds={tracking?.etaSeconds}
          />
            : <CurrentActivity recallConfig={recallConfig} currentActivity={currentActivity} parkingName={bookingDetails?.site.name || '-'} />
          }
          {position &&
            <BottomSheet isOpen={true}>
              <AdditionalInfo bookingDetails={bookingDetails} currentActivity={currentActivity} parkingName={bookingDetails?.site.name || '-'} otp={bookingDetails?.otp || null} />
              {shouldShowPaymentAtExitInfo && <PaymentInfo paymentDetails={bookingDetails?.payment || {}} currentActivity={currentActivity} />}
              <VisibleActivities draggable currentActivity={currentActivity} visibleActivities={visibleActivities} activitiesPercentage={activitiesPercentage} />
              <Feedback currentActivity={currentActivity} activitiesPercentage={activitiesPercentage} alreadyRated={bookingDetails?.rating > 0} rating={rating} setRating={setRating} />
              {shouldShowPaymentAtExitInfo && <ExitInfo />}
              <BookingDetails bookingDetails={bookingDetails} isPaid={isPaid} showUserInfo={showUserInfo} showPaymentInfo={showPaymentInfo} />
            </BottomSheet>
          }
          {shouldShowRecall && <Recall recallConfig={recallConfig} bookingDetails={bookingDetails} refresh={() => fetchData()} />}
          {shouldShowPayment && <Payments bookingDetails={bookingDetails} refresh={() => fetchData()} />}
          {/* <FeedbackHandler bookingId={bookingDetails.id} currentActivity={currentActivity} alreadyRated={bookingDetails.rating > 0} refresh={() => fetchData()} rating={rating} /> */}
          <Activities activities={activities} currentActivity={currentActivity} activitiesPercentage={activitiesPercentage} />
          {/* <ImageViewer/> */}
        </>
        :
        loading && !currentActivity && !bookingDetails
          ? <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>
            <CircularProgress size={50} style={{ color: "#18CC69" }} />
          </Box>
          : !loading && !currentActivity && !bookingDetails
            ? <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>
              <CircularProgress size={50} style={{ color: "#18CC69" }} />
            </Box>
            : null
      }
      <ToastContainer position="bottom-right" newestOnTop={true} closeOnClick={true} rtl={false} pauseOnFocusLoss draggable={false} theme="colored" icon={false} autoClose={5000} pauseOnHover />
    </Box>
  )
}

export default Main
